package gr.tech.GRAttestation.components.modals

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import gr.tech.GRAttestation.util.ROUTES
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.url.URL
import org.w3c.fetch.INCLUDE
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit

// --- Blob + Object URL lifecycle ---
// A Blob is a chunk of raw bytes held in the browser's native memory (outside the JS heap).
// When you call URL.createObjectURL(blob), the browser registers that blob in an internal
// URL registry and returns a blob:// string like "blob://localhost/<uuid>". The iframe can
// then use that string as its src — no network request, no auth needed.
//
// Why setting blobUrl = null alone is not enough to free the memory:
// The Kotlin variable `blobUrl` is just a String ("blob://localhost/<uuid>"). Setting it to
// null drops the String, but the browser's URL registry still holds its own reference to the
// blob bytes under that UUID. The JS garbage collector never sees that registry — it lives in
// the browser's native (C++) layer — so the bytes stay pinned in memory indefinitely.
//
// URL.revokeObjectURL(url) removes the entry from the browser's registry, which releases the
// last reference to the blob bytes and lets the browser free that native memory. You must call
// it explicitly: once when loading a new PDF (to drop the previous one) and once on dispose
// (to drop the final one when the composable leaves the tree).
@Composable
fun PreviewPDFModal(
    isOpen: Boolean,
    attachmentId: Int?,
    onClose: () -> Unit
) {
    var blobUrl: String? by remember { mutableStateOf(null) }
    var isLoading by remember { mutableStateOf(false) }
    var hasError by remember { mutableStateOf(false) }

    LaunchedEffect(attachmentId) {
        // If a previous PDF was loaded, free its blob from browser memory before fetching the new one
        blobUrl?.let { URL.revokeObjectURL(it) }
        blobUrl = null
        hasError = false

        if (attachmentId == null) return@LaunchedEffect

        isLoading = true
        try {
            // credentials: INCLUDE sends the session cookie, same as all other API calls.
            // This sidesteps the iframe cross-origin cookie problem — auth happens here in JS,
            // not inside the iframe navigation which can't attach credentials.
            val response = window.fetch(
                "${ROUTES.BACKEND.FORMS.ADMIN_FORM_DOWNLOAD}/$attachmentId",
                RequestInit(credentials = RequestCredentials.INCLUDE)
            ).await()
            if (response.ok) {
                // response.blob() reads the raw bytes from the response body.
                // URL.createObjectURL() registers those bytes in the browser under a temporary
                // blob:// URL (e.g. blob://localhost/some-uuid). The iframe src is then set to
                // that local URL — no network request, no auth needed, no cross-origin issue.
                blobUrl = URL.createObjectURL(response.blob().await())
            } else {
                hasError = true
            }
        } catch (e: Exception) {
            hasError = true
        }
        isLoading = false
    }

    DisposableEffect(Unit) {
        // Blobs registered with createObjectURL persist in browser memory until explicitly freed
        // or the tab closes. revokeObjectURL drops the reference so the bytes can be GC'd.
        onDispose { blobUrl?.let { URL.revokeObjectURL(it) } }
    }

    ModalOverlay(isOpen = isOpen, onClose = onClose) {
        Div(
            attrs = Modifier
                .classNames("card")
                .fillMaxWidth()
                .maxWidth(1100.px)
                .attrsModifier { onClick { it.stopPropagation() } }
                .toAttrs()
        ) {
            Div(
                attrs = Modifier
                    .classNames("card-header", "d-flex", "justify-content-between", "align-items-center")
                    .toAttrs()
            ) {
                H5 { Text("PDF Preview") }
                Button(
                    attrs = Modifier
                        .classNames("btn-close")
                        .onClick { onClose() }
                        .toAttrs { attr("aria-label", "Close") }
                ) {}
            }
            Div(
                attrs = Modifier
                    .classNames("card-body")
                    .padding(0.px)
                    .toAttrs()
            ) {
                when {
                    isLoading -> Div(
                        attrs = Modifier
                            .classNames("d-flex", "justify-content-center", "align-items-center")
                            .height(400.px)
                            .toAttrs()
                    ) {
                        Div(attrs = Modifier.classNames("spinner-border", "text-secondary").toAttrs()) {
                            Span(attrs = Modifier.classNames("visually-hidden").toAttrs()) { Text("Loading...") }
                        }
                    }
                    hasError -> Div(
                        attrs = Modifier
                            .classNames("d-flex", "justify-content-center", "align-items-center", "text-danger")
                            .height(400.px)
                            .toAttrs()
                    ) {
                        Text("Failed to load PDF.")
                    }
                    blobUrl != null -> Iframe(
                        attrs = {
                            attr("src", blobUrl!!)
                            style {
                                width(100.percent)
                                height(80.vh)
                                property("border", "none")
                            }
                        }
                    )
                }
            }
        }
    }
}
